import { useEffect, useSyncExternalStore } from "react";
import { getAuthData } from "Utils/storage";
import { viewProviderTool } from "Services/Provider/providerService";
import { consents as consentSeed, dataCategoriesSeed, noticeGroups, processorsSeed, purposes as purposeSeed, requests as requestSeed, type CategoryStorage, type NoticeGroup } from "./data";
import {
  captureConsentPayloads,
  consentQueryFilters,
  currentConsentRows,
  groupNotices,
  languageRows,
  nextCode,
  toCategory,
  toProcessor,
  toPurpose,
  type ApiCategory,
  type ApiConsent,
  type ApiNotice,
  type ApiProcessor,
  type ApiPurpose,
  type ConsentListFilters,
  type ConsentRecord,
} from "./catalog-map";
import { principalHeaders, resolvePrincipal, type ConsentPrincipal } from "./principal";

const damOrigin = "https://api.trafyn.info/dam/api/v1/realms";
const damConsentOrigin = "https://api.trafyn.info/dam/api/v1/consents";
const queryOrigin = "https://api.trafyn.info/query-service/api/v1/realms";
const publicRealmId = process.env.REACT_APP_CONSENT_PUBLIC_REALM_ID || "";

function session() {
  const auth = getAuthData() as { realmIdentifier?: string; systemUserId?: string | number; token?: string } | null;
  return {
    realmId: auth?.realmIdentifier ?? publicRealmId,
    userId: auth?.systemUserId == null ? "" : String(auth.systemUserId),
    token: auth?.token || "",
  };
}

function damBase() {
  const { realmId, userId } = session();
  return `${damOrigin}/${realmId}/users/${userId}`;
}

function queryBase() {
  const { realmId, userId } = session();
  return `${queryOrigin}/${realmId}/users/${userId}`;
}

export type Purpose = ReturnType<typeof toPurpose>;
export type CatalogResource = "data-categories" | "processors" | "purposes" | "notices" | "consents";

type Fetched = Record<CatalogResource, boolean>;

const resources: CatalogResource[] = ["data-categories", "processors", "purposes", "notices", "consents"];

function fetchedAs(value: boolean): Fetched {
  return { "data-categories": value, processors: value, purposes: value, notices: value, consents: value };
}

type State = {
  purposes: Purpose[];
  categories: ReturnType<typeof toCategory>[];
  processors: ReturnType<typeof toProcessor>[];
  notices: NoticeGroup[];
  consents: ConsentRecord[];
  loading: boolean;
  error: string;
  mockData: boolean;
  fetched: Fetched;
};

const mockPreferenceKey = "ninjacart.mockData";

function readMockPreference() {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(mockPreferenceKey) === "on";
}

function writeMockPreference(on: boolean) {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(mockPreferenceKey, on ? "on" : "off");
}

function seedCatalog(): State {
  return {
    purposes: structuredClone(purposeSeed).map((purpose) => ({ ...purpose, mappedNotices: [], names: purpose.names ?? {}, mandatory: false })),
    categories: structuredClone(dataCategoriesSeed).map((category) => ({ ...category, usedBy: [], names: category.names ?? {}, stores: category.stores ?? [] })),
    processors: structuredClone(processorsSeed).map((processor) => ({ ...processor, usedBy: [] })),
    notices: structuredClone(noticeGroups),
    consents: structuredClone(consentSeed),
    loading: false,
    error: "",
    mockData: true,
    fetched: fetchedAs(true),
  };
}

function liveCatalog(): State {
  return {
    purposes: [],
    categories: [],
    processors: [],
    notices: [],
    consents: [],
    loading: false,
    error: "",
    mockData: false,
    fetched: fetchedAs(false),
  };
}

let state: State = liveCatalog();
let preferenceApplied = false;
const purposeRecordIds = new Map<string, string>();
const categoryRecordIds = new Map<string, string>();
const listeners = new Set<() => void>();
const inflight = new Map<CatalogResource, Promise<void>>();
const generation = new Map<CatalogResource, number>();

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
function emit() { listeners.forEach((fn) => fn()); }
function set(next: Partial<State>) { state = { ...state, ...next }; emit(); }

function message(error: unknown) {
  return error instanceof Error ? error.message : "Request failed";
}

function authHeaders(): Record<string, string> {
  const auth = getAuthData() as { token?: string; token_type?: string; ownerUserId?: string | number; systemUserId?: string | number } | null;
  if (!auth?.token) return {};
  return {
    Authorization: `${auth.token_type || "Bearer"} ${auth.token}`,
    "x-nc-provider-id": "Ninjacart",
    ...(auth.ownerUserId == null ? {} : { "x-nc-user-id": String(auth.ownerUserId) }),
    ...(auth.systemUserId == null ? {} : { "x-nc-system-user-id": String(auth.systemUserId) }),
  };
}

async function send<T>(path: string, method: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
  const init: RequestInit = {
    method,
    headers: { ...(body === undefined ? {} : { "Content-Type": "application/json" }), ...authHeaders(), ...headers },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const response = await fetch(`${damBase()}/${path}`, init);
  const text = await response.text();
  if (!response.ok) throw new Error(text || `${method} ${path} failed (${response.status})`);
  return text ? JSON.parse(text) as T : undefined as T;
}

async function sendConsentCapture<T>(path: string, method: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
  const init: RequestInit = {
    method,
    headers: {
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      ...(publicRealmId ? { "x-nc-realm-id": publicRealmId } : {}),
      ...headers,
    },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const url = path ? `${damConsentOrigin}/${path}` : damConsentOrigin;
  const response = await fetch(url, init);
  const text = await response.text();
  if (!response.ok) throw new Error(text || `${method} consents/${path} failed (${response.status})`);
  return text ? JSON.parse(text) as T : undefined as T;
}

async function sendQuery<T>(path: string, method: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    headers: { ...(body === undefined ? {} : { "Content-Type": "application/json" }), ...authHeaders() },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const response = await fetch(`${queryBase()}/${path}`, init);
  const text = await response.text();
  if (!response.ok) throw new Error(text || `${method} ${path} failed (${response.status})`);
  return text ? JSON.parse(text) as T : undefined as T;
}

export type QueryDataSourceOption = { id: string; name: string };

const userDataRedashDataSourceIds = new Set(["107", "96", "93", "83", "231", "246", "251"]);

export function listQueryDataSources(): Promise<QueryDataSourceOption[]> {
  return sendQuery<{ data?: { configurations?: { id?: string | number | null; externalId?: string | number | null; name?: string | null }[] } }>(
    "data-source-configurations?limit=500&provider=REDASH",
    "GET",
  ).then((body) => (body.data?.configurations ?? []).flatMap((row) => {
    const id = String(row.externalId ?? row.id ?? "").trim();
    if (!id || id === "null" || id === "undefined" || !userDataRedashDataSourceIds.has(id)) return [];
    const name = String(row.name ?? "").trim();
    return [{ id, name: name || id }];
  }));
}

export function testStorageQuery(input: { dataSourceId: string; query: string; parameters: Record<string, string> }) {
  return sendQuery<{ data?: { status?: string; error?: string; result?: unknown } }>("query-sessions/actions/execute", "POST", {
    externalId: input.dataSourceId,
    query: input.query,
    parameters: input.parameters,
    mode: "SYNC",
  }).then((body) => {
    const status = body.data?.status;
    if (status === "SUCCESS") return body.data?.result ?? null;
    throw new Error(body.data?.error?.trim() || `Query test failed (${status || "unknown"})`);
  });
}

export type ProviderToolChoice = { providerId: string; providerName: string; toolId: string; toolName: string };

function providerCatalogRows(response: unknown, key: "Provider" | "Tool") {
  const body = response as { data?: { status?: number; data?: Record<string, { id?: string; name?: string; providerId?: string }>[] } };
  if (body?.data?.status !== 200) return [];
  return (body.data?.data ?? []).flatMap((row) => {
    const entity = row[key];
    return entity?.id ? [entity] : [];
  });
}

const consentToolStorageKey = "ninjacart.consentTool";

function readConsentTools() {
  try {
    const parsed = JSON.parse(localStorage.getItem(consentToolStorageKey) || "{}") as Record<string, unknown>;
    return Object.fromEntries(Object.entries(parsed).flatMap(([notice, value]) => {
      if (typeof value === "string" && value.trim()) return [[notice, { id: value.trim(), name: "" }]];
      if (value && typeof value === "object" && typeof (value as { id?: unknown }).id === "string" && (value as { id: string }).id.trim()) {
        const name = (value as { name?: unknown }).name;
        return [[notice, { id: (value as { id: string }).id.trim(), name: typeof name === "string" ? name.trim() : "" }]];
      }
      return [];
    }));
  } catch {
    return {};
  }
}

export function rememberConsentTool(noticeId: string, toolId: string, toolName = "") {
  const notice = noticeId.trim();
  const tool = toolId.trim();
  if (!notice || !tool) return;
  const current = readConsentTools()[notice];
  const name = toolName.trim() || (current?.id === tool ? current.name : "");
  localStorage.setItem(consentToolStorageKey, JSON.stringify({ ...readConsentTools(), [notice]: { id: tool, name } }));
}

export function consentToolForNotice(noticeId: string) {
  return readConsentTools()[noticeId.trim()]?.id || "";
}

export function consentToolNameForNotice(noticeId: string) {
  return readConsentTools()[noticeId.trim()]?.name || "";
}

export function loadProviderTools(): Promise<ProviderToolChoice[]> {
  return viewProviderTool({ input: { entityType: "Provider", property: "id", value: "1", operator: "neq" } }).then((providerResponse) => {
    const providers = providerCatalogRows(providerResponse, "Provider");
    return Promise.all(providers.map((provider) => viewProviderTool({ input: { entityType: "Tool", property: "providerId", value: provider.id, operator: "or" } }).then((toolResponse) =>
      providerCatalogRows(toolResponse, "Tool").map((tool) => ({
        providerId: provider.id || "",
        providerName: provider.name || provider.id || "Provider",
        toolId: tool.id || "",
        toolName: tool.name || tool.id || "",
      }))
    ))).then((groups) => groups.flat().sort((left, right) => left.providerName.localeCompare(right.providerName) || left.toolName.localeCompare(right.toolName)));
  });
}

export function noteConsentEvent(input: { event: string; userId: string; toolId: string; channel?: string; purpose?: string; purposeName?: string; noticeId?: string; noticeName?: string; noticeVersion?: string; language?: string; principal?: ConsentPrincipal }) {
  const { realmId } = session();
  const principal = input.principal ?? { id: input.userId, type: "USER" as const };
  const body = {
    event: input.event,
    realmId,
    userId: principal.id,
    purpose: input.purpose || "",
    purposeName: input.purposeName || "",
    noticeId: input.noticeId || "",
    noticeName: input.noticeName || "",
    noticeVersion: input.noticeVersion || "",
    language: input.language || "",
    toolId: input.toolId,
    channel: input.channel?.trim() || input.toolId,
    captureMethod: "Checkbox",
  };
  const headers = { "x-nc-tool-id": input.toolId, ...principalHeaders(principal) };
  return sendConsentCapture("events", "POST", body, headers);
}

export function recordConsents(input: {
  userId: string;
  principal?: ConsentPrincipal;
  applicationId: string;
  applicationName: string;
  noticeName: string;
  noticeCode?: string;
  noticeRecordId?: string;
  noticeVersion?: string;
  language: string;
  channel?: string;
  choices: { purposeId: string; purposeName: string; category?: string; categories?: string[]; processors?: string[]; retentionDays?: string; granted: boolean }[];
}) {
  const { realmId, userId } = session();
  const channel = input.channel?.trim() || input.applicationId;
  const principal = input.principal ?? { id: input.userId, type: "USER" as const };
  const payloads = captureConsentPayloads({
    ...input,
    userId: principal.id,
    principalType: principal.type,
    principalSource: principal.source,
    channel,
    realmId: realmId || publicRealmId,
    operatorUserId: userId || "public-consent",
    grantedAt: new Date().toISOString(),
  });
  const skipped = input.choices.filter((choice) => !choice.granted);
  const headers = { "x-nc-tool-id": input.applicationId, ...principalHeaders(principal) };
  return (async () => {
    for (const payload of payloads) {
      await sendConsentCapture("", "POST", payload, headers);
    }
    for (const choice of skipped) await noteConsentEvent({
      event: "Consent not given",
      userId: principal.id,
      principal,
      toolId: input.applicationId,
      channel,
      purpose: choice.purposeId,
      purposeName: choice.purposeName,
      noticeId: input.noticeCode || input.noticeRecordId,
      noticeName: input.noticeName,
      noticeVersion: input.noticeVersion,
      language: input.language,
    });
  })();
}

async function search<T>(resource: string, extraFilters: { path: string; property: string; operator: string; value: string | string[] }[] = [], _principal?: ConsentPrincipal) {
  // ponytail: one page of 200. DAM treats offset as a page index (0 and 1 are both the first page), so raise this only with offset 2, 3, ...
  const filters = [...(resource === "consents" ? [{ path: "Consent", property: "type", operator: "eq", value: "DPDPA_PURPOSE_CONSENT" }] : []), ...extraFilters];
  const payload = { limit: 200, offset: 0, filters, globalSearch: true, sort: "id", sortOrder: "desc" };
  const body = await send<{ data?: T[] }>(`${resource}/search`, "POST", payload);
  return body.data ?? [];
}

function parseCaptureContext(body: { data?: { showNotice?: boolean; purposes?: { code?: string; name?: string; status?: string; consentId?: string; consentedOnThisVersion?: boolean }[] } }) {
  const purposes = body.data?.purposes ?? [];
  const openPurposeCodes = purposes.flatMap((purpose) => purpose.code && purpose.consentedOnThisVersion !== true ? [purpose.code] : []);
  return {
    showNotice: body.data?.showNotice !== false || purposes.some((purpose) => purpose.status === "REVOKED"),
    openPurposeCodes,
    activeConsents: purposes.flatMap((purpose) => purpose.consentId && (purpose.status === "GRANTED" || purpose.status === "APPROVED") && purpose.consentedOnThisVersion ? [{ code: purpose.code ?? "", name: purpose.name ?? "", consentId: String(purpose.consentId) }] : []),
  };
}

export async function captureContextForNotice(input: { userId: string; toolId: string; noticeCode: string; noticeRecordId?: string; principal?: ConsentPrincipal }) {
  const pack = await loadNoticeCapturePackage(input.noticeCode, input.principal);
  return pack.capture ?? { showNotice: true, openPurposeCodes: null as string[] | null, activeConsents: [] as { code: string; name: string; consentId: string }[] };
}

export async function loadNoticeCapturePackage(noticeCode: string, principal?: ConsentPrincipal) {
  const dataPrincipal = principal ?? resolvePrincipal();
  const body = await sendConsentCapture<{
    data?: {
      notice?: ApiNotice;
      purposes?: ApiPurpose[];
      categories?: ApiCategory[];
      processors?: ApiProcessor[];
      toolId?: string;
      capture?: { showNotice?: boolean; purposes?: { code?: string; name?: string; status?: string; consentId?: string; consentedOnThisVersion?: boolean }[] };
    };
  }>("capture-context", "POST", { noticeCode }, principalHeaders(dataPrincipal));
  const notice = body.data?.notice;
  const apiPurposes = body.data?.purposes ?? [];
  const categories = (body.data?.categories ?? []).map(toCategory);
  const processors = (body.data?.processors ?? []).map(toProcessor);
  const categoryNameByCode = new Map(categories.map((category) => [category.id, category.name]));
  const processorNameByCode = new Map(processors.map((processor) => [processor.id, processor.name]));
  const purposes = apiPurposes.map((row) => toPurpose(row, categoryNameByCode, processorNameByCode));
  const purposeCodeByRecordId = new Map(apiPurposes.flatMap((row) => row.id == null ? [] : [[String(row.id), row.code]] as const));
  return {
    notices: notice ? groupNotices([notice], purposeCodeByRecordId) : [],
    purposes,
    categories,
    toolId: body.data?.toolId || "",
    capture: body.data?.capture ? parseCaptureContext({ data: body.data.capture }) : null,
  };
}

export type ConsentAuditRow = {
  id: string;
  event: string;
  userId: string;
  purpose: string;
  purposeName?: string;
  noticeId: string;
  noticeName: string;
  noticeVersion: string;
  language: string;
  toolId: string;
  channel: string;
  captureMethod: string;
  occurredAt: string;
};

export function searchConsentAudit(filters: { userId?: string; toolId?: string; notice?: string; from?: string; to?: string }) {
  return send<{ data?: ConsentAuditRow[] }>("consents/audit-log", "POST", filters).then((body) => body.data ?? []);
}

export function searchConsentRecords(filters: ConsentListFilters, principal?: ConsentPrincipal) {
  const extra = [
    ...consentQueryFilters(filters).map((filter) => ({ path: "Consent", ...filter })),
    ...containsFilters("Consent", ["userId", "requesteeUserId", "toolId", "name", "description", "purpose", "category", "channel"], filters.text),
  ];
  return search<ApiConsent>("consents", extra, principal).then((rows) => currentConsentRows(rows));
}

function containsFilters(path: string, fields: string[], text?: string) {
  const term = text?.trim();
  if (!term) return [];
  return fields.map((property) => ({ path, property, operator: "contains", value: term }));
}

function purposeNameMaps() {
  return {
    categoryNameByCode: new Map(state.categories.map((category) => [category.id, category.name])),
    processorNameByCode: new Map(state.processors.map((processor) => [processor.id, processor.name])),
  };
}

export function searchPurposes(text: string) {
  const names = purposeNameMaps();
  return search<ApiPurpose>("purposes", containsFilters("Purpose", ["name", "code"], text)).then((rows) => rows.map((row) => toPurpose(row, names.categoryNameByCode, names.processorNameByCode)));
}

export function searchCategories(text: string) {
  return search<ApiCategory>("data-categories", containsFilters("DataCategory", ["name", "code", "groupName", "example"], text)).then((rows) => rows.map(toCategory));
}

export function searchProcessors(text: string) {
  return search<ApiProcessor>("processors", containsFilters("Processor", ["name", "code", "role", "location"], text)).then((rows) => rows.map(toProcessor));
}

export function searchNotices(text: string) {
  const purposeCodeByRecordId = new Map([...purposeRecordIds.entries()].map(([code, id]) => [id, code]));
  return search<ApiNotice>("notices", containsFilters("Notice", ["name", "code", "description", "owner"], text)).then((rows) => groupNotices(rows, purposeCodeByRecordId));
}

export function setMockData(on: boolean) {
  if (on === state.mockData) return;
  writeMockPreference(on);
  resources.forEach((resource) => generation.set(resource, (generation.get(resource) ?? 0) + 1));
  inflight.clear();
  if (on) {
    state = seedCatalog();
    emit();
    return;
  }
  set({ mockData: false, loading: false, error: "", purposes: [], categories: [], processors: [], notices: [], consents: [], fetched: fetchedAs(false) });
}

function othersInFlight(resource: CatalogResource) {
  for (const key of inflight.keys()) if (key !== resource) return true;
  return false;
}

export function loadResource(resource: CatalogResource, force = false) {
  if (state.mockData) return Promise.resolve();
  if (!force && state.fetched[resource]) return Promise.resolve();
  const pending = inflight.get(resource);
  if (pending && !force) return pending;
  const run = fetchResource(resource).finally(() => {
    if (inflight.get(resource) === run) inflight.delete(resource);
  });
  inflight.set(resource, run);
  return run;
}

export function reloadResources(requested: readonly CatalogResource[]) {
  return Promise.all(requested.map((resource) => loadResource(resource, true)));
}

async function fetchResource(resource: CatalogResource) {
  const ticket = (generation.get(resource) ?? 0) + 1;
  generation.set(resource, ticket);
  set({ loading: true, error: "" });
  const current = () => state.mockData || generation.get(resource) !== ticket;
  try {
    if (resource === "data-categories") {
      const categories = await search<ApiCategory>(resource);
      if (current()) return;
      categoryRecordIds.clear();
      categories.forEach((row) => categoryRecordIds.set(row.code, String(row.id)));
      const mapped = categories.map(toCategory);
      const nameByCode = new Map(mapped.map((category) => [category.id, category.name]));
      const purposes = state.purposes.map((purpose) => ({ ...purpose, pii: purpose.pii.map((item) => nameByCode.get(item) ?? item) }));
      set({ categories: mapped, purposes, loading: othersInFlight(resource), error: "", mockData: false, fetched: { ...state.fetched, [resource]: true } });
      return;
    }
    if (resource === "processors") {
      const processors = await search<ApiProcessor>(resource);
      if (current()) return;
      set({ processors: processors.map(toProcessor), loading: othersInFlight(resource), error: "", mockData: false, fetched: { ...state.fetched, [resource]: true } });
      return;
    }
    if (resource === "purposes") {
      await Promise.all([loadResource("data-categories"), loadResource("processors")]);
      const purposes = await search<ApiPurpose>(resource);
      if (current()) return;
      const categoryNameByCode = new Map(state.categories.map((category) => [category.id, category.name]));
      const processorNameByCode = new Map(state.processors.map((processor) => [processor.id, processor.name]));
      purposeRecordIds.clear();
      purposes.forEach((row) => purposeRecordIds.set(row.code, String(row.id)));
      set({ purposes: purposes.map((row) => toPurpose(row, categoryNameByCode, processorNameByCode)), loading: othersInFlight(resource), error: "", mockData: false, fetched: { ...state.fetched, [resource]: true } });
      return;
    }
    if (resource === "consents") {
      const consents = await search<ApiConsent>(resource);
      if (current()) return;
      set({ consents: currentConsentRows(consents), loading: othersInFlight(resource), error: "", mockData: false, fetched: { ...state.fetched, [resource]: true } });
      return;
    }
    const notices = await search<ApiNotice>(resource);
    if (current()) return;
    const purposeCodeByRecordId = new Map([...purposeRecordIds.entries()].map(([code, id]) => [id, code]));
    set({ notices: groupNotices(notices, purposeCodeByRecordId), loading: othersInFlight(resource), error: "", mockData: false, fetched: { ...state.fetched, [resource]: true } });
  } catch (error) {
    if (current()) return;
    set({ loading: othersInFlight(resource), error: message(error) });
  }
}

export function useRegistry() {
  const snapshot = useSyncExternalStore(subscribe, () => state, () => state);
  useEffect(() => {
    if (preferenceApplied) return;
    preferenceApplied = true;
    const saved = readMockPreference();
    if (saved !== state.mockData) setMockData(saved);
  }, []);
  return snapshot;
}

export function useCatalog(requested: readonly CatalogResource[]) {
  const snapshot = useRegistry();
  const key = requested.join(",");
  useEffect(() => {
    if (snapshot.mockData || !key) return;
    key.split(",").forEach((resource) => void loadResource(resource as CatalogResource));
  }, [key, snapshot.mockData]);
  return snapshot;
}

async function change(resource: CatalogResource, run: () => Promise<void>) {
  try {
    await run();
  } catch (error) {
    set({ loading: false, error: message(error) });
    throw error;
  }
  inflight.delete(resource);
  await loadResource(resource, true);
}

export function addCategory(input: { name: string; group: string; example: string; sensitive: string; names?: Parameters<typeof languageRows>[0]; stores?: CategoryStorage[] }) {
  const code = nextCode("DC", state.categories.map((category) => category.id));
  const names = input.names ?? {};
  const stores = input.stores ?? [];
  if (state.mockData) {
    set({ categories: [...state.categories, { id: code, name: input.name, group: input.group, example: input.example, sensitive: input.sensitive, names, stores }] });
    return Promise.resolve();
  }
  return change("data-categories", () => send("data-categories", "POST", {
    code,
    name: input.name,
    groupName: input.group,
    example: input.example,
    sensitive: input.sensitive === "Yes",
    languages: languageRows(names),
    ...(stores.length ? { stores } : {}),
  }));
}

export function updateCategory(id: string, input: { name: string; group: string; example: string; sensitive: string; names?: Parameters<typeof languageRows>[0]; stores?: CategoryStorage[] }) {
  const names = input.names ?? {};
  const stores = input.stores ?? [];
  if (state.mockData) {
    set({
      categories: state.categories.map((category) => category.id === id
        ? { ...category, name: input.name, group: input.group, example: input.example, sensitive: input.sensitive, names, stores }
        : category),
    });
    return Promise.resolve();
  }
  const recordId = categoryRecordIds.get(id);
  if (!recordId) {
    const error = new Error(`Data category ${id} is not loaded`);
    set({ error: error.message });
    return Promise.reject(error);
  }
  return change("data-categories", () => send(`data-categories/${recordId}`, "PUT", {
    code: id,
    name: input.name,
    groupName: input.group,
    example: input.example,
    sensitive: input.sensitive === "Yes",
    languages: languageRows(names),
    stores,
  }));
}

export function deleteCategory(id: string) {
  if (state.mockData) {
    set({ categories: state.categories.filter((category) => category.id !== id) });
    return Promise.resolve();
  }
  const recordId = categoryRecordIds.get(id);
  if (!recordId) {
    const error = new Error(`Data category ${id} is not loaded`);
    set({ error: error.message });
    return Promise.reject(error);
  }
  return change("data-categories", () => send(`data-categories/${recordId}`, "DELETE"));
}

export function addProcessor(input: { name: string; role: string; location: string; crossBorder: string; contract: string }) {
  const code = nextCode("TP", state.processors.map((processor) => processor.id));
  if (state.mockData) {
    set({ processors: [...state.processors, { id: code, ...input }] });
    return Promise.resolve();
  }
  return change("processors", () => send("processors", "POST", {
    code,
    name: input.name,
    role: input.role,
    location: input.location,
    crossBorder: input.crossBorder === "Yes",
    contract: input.contract,
  }));
}

export function addPurpose(input: { name: string; principal: string; basis: string; pii: string[]; recipients: string[]; retention: string; pwd: string; mandatory?: boolean; names?: Parameters<typeof languageRows>[0] }) {
  const code = nextCode("P", state.purposes.map((purpose) => purpose.id));
  const names = input.names ?? {};
  if (state.mockData) {
    set({ purposes: [...state.purposes, { id: code, name: input.name, principal: input.principal, basis: input.basis, pii: input.pii, recipients: input.recipients.join(", "), retention: input.retention, pwd: input.pwd, mandatory: input.mandatory === true, reviewed: "Meera Iyer · 17 Sep 2026", mappedNotices: [], names }] });
    return Promise.resolve();
  }
  const categoryCode = (name: string) => state.categories.find((category) => category.name === name)?.id ?? name;
  const processorCode = (name: string) => state.processors.find((processor) => processor.name === name)?.id ?? name;
  return change("purposes", () => send("purposes", "POST", {
    code,
    name: input.name,
    basis: input.basis,
    retention: input.retention,
    pwd: input.pwd,
    mandatory: input.mandatory === true,
    categories: input.pii.map((name) => ({ categoryCode: categoryCode(name) })),
    processors: input.recipients.map((name) => ({ processorCode: processorCode(name) })),
    principals: [{ principal: input.principal }],
    languages: languageRows(names),
  }));
}

export function deletePurpose(id: string) {
  if (state.mockData) {
    set({ purposes: state.purposes.filter((purpose) => purpose.id !== id) });
    return Promise.resolve();
  }
  const recordId = purposeRecordIds.get(id);
  if (!recordId) {
    const error = new Error(`Purpose ${id} is not loaded`);
    set({ error: error.message });
    return Promise.reject(error);
  }
  return change("purposes", () => send(`purposes/${recordId}`, "DELETE"));
}

function purposeSnapshots(codes: string[]) {
  return codes.map((code) => {
    const purpose = state.purposes.find((item) => item.id === code);
    const recordId = purposeRecordIds.get(code);
    if (!purpose || !recordId) throw new Error(`Purpose ${code} is not loaded`);
    return { purposeId: recordId, code: purpose.id, name: purpose.name, basis: purpose.basis, retention: purpose.retention, mandatory: purpose.mandatory === true };
  });
}

export function addNotice(input: { name: string; description: string; owner: string; effectiveOn: string; languages: { languageCode: string; title: string; body: string }[]; purposeCodes: string[]; toolId: string }) {
  const code = nextCode("N", state.notices.map((notice) => notice.id));
  const links = input.purposeCodes.join(" · ");
  const toolId = input.toolId.trim();
  if (state.mockData) {
    set({ notices: [...state.notices, { id: code, name: input.name, description: input.description, owner: input.owner, versions: [{ version: "v1.0", status: "Draft", date: input.effectiveOn, languages: input.languages.map((language) => language.languageCode), texts: input.languages.map((language) => ({ language: language.languageCode, title: language.title, body: language.body })), ids: input.purposeCodes, links, toolId }] }] });
    return Promise.resolve(code);
  }
  return change("notices", async () => {
    await send("notices", "POST", {
      code,
      name: input.name,
      description: input.description,
      owner: input.owner,
      noticeVersion: "v1.0",
      status: "Draft",
      effectiveOn: input.effectiveOn,
      languages: input.languages,
      purposes: purposeSnapshots(input.purposeCodes),
      ...(toolId ? { tools: [{ toolId }] } : {}),
    });
  }).then(() => code);
}

export function addNoticeVersion(input: { code: string; name: string; description: string; owner: string; noticeVersion: string; effectiveOn: string; languages: { languageCode: string; title: string; body: string }[]; purposeCodes: string[]; toolId: string }) {
  const toolId = input.toolId.trim();
  if (state.mockData) {
    const links = input.purposeCodes.join(" · ");
    set({
      notices: state.notices.map((notice) => notice.id === input.code ? {
        ...notice,
        owner: input.owner,
        versions: [{ version: input.noticeVersion, status: "Draft", date: input.effectiveOn, languages: input.languages.map((language) => language.languageCode), texts: input.languages.map((language) => ({ language: language.languageCode, title: language.title, body: language.body })), ids: input.purposeCodes, links, toolId }, ...notice.versions],
      } : notice),
    });
    return Promise.resolve();
  }
  const purposes = purposeSnapshots(input.purposeCodes);
  return change("notices", () => send("notices", "POST", {
    code: input.code,
    name: input.name,
    description: input.description,
    owner: input.owner,
    noticeVersion: input.noticeVersion,
    status: "Draft",
    effectiveOn: input.effectiveOn,
    languages: input.languages,
    purposes,
    ...(toolId ? { tools: [{ toolId }] } : {}),
  }));
}

export function stampNoticeEffectiveOn(code: string, noticeVersion: string, effectiveOn: string) {
  if (state.mockData) {
    set({
      notices: state.notices.map((notice) => notice.id !== code ? notice : {
        ...notice,
        versions: notice.versions.map((item) => item.version === noticeVersion ? { ...item, date: effectiveOn } : item),
      }),
    });
    return Promise.resolve();
  }
  const group = state.notices.find((notice) => notice.id === code);
  const version = group?.versions.find((item) => item.version === noticeVersion);
  if (!group || !version?.recordId) {
    const error = new Error("Notice version is not loaded");
    set({ error: error.message });
    return Promise.reject(error);
  }
  return change("notices", () => send(`notices/${version.recordId}`, "PUT", { code, noticeVersion, effectiveOn }));
}

export function publishNotice(code: string, noticeVersion: string) {
  if (state.mockData) {
    set({
      notices: state.notices.map((notice) => notice.id !== code ? notice : {
        ...notice,
        versions: notice.versions.map((item) => {
          if (item.version === noticeVersion) return { ...item, status: "Published" };
          if (item.status === "Published") return { ...item, status: "Superseded" };
          return item;
        }),
      }),
    });
    return Promise.resolve();
  }
  const group = state.notices.find((notice) => notice.id === code);
  const version = group?.versions.find((item) => item.version === noticeVersion);
  if (!group || !version?.recordId) {
    const error = new Error("Notice version is not loaded");
    set({ error: error.message });
    return Promise.reject(error);
  }
  return change("notices", () => send(`notices/${version.recordId}/publish`, "POST"));
}

export function noticesUsingPurpose(id: string) {
  const used: string[] = [];
  state.notices.forEach((group) => group.versions.forEach((version) => {
    if (version.ids.includes(id)) used.push(`${group.id} ${group.name} · ${version.version}`);
  }));
  return used;
}

export function purposesUsingCategory(name: string) {
  return state.purposes.filter((purpose) => purpose.pii.includes(name)).map((purpose) => purpose.id);
}

export function purposesUsingProcessor(name: string) {
  return state.purposes.filter((purpose) => purpose.recipients.includes(name)).map((purpose) => purpose.id);
}

const workflowBaseUrl = "https://api.trafyn.info/workflow-engine";
const workflowRealmId = "dd180bca-465a-470a-abe4-9d5a15ded551";
const workflowToolId = "6de0a6ef-2755-4cf6-817b-1d5ef0c7324c";
const grievanceProcessId = 70;

export type UserTicket = {
  id: string;
  processInstanceId?: number;
  userId: string;
  type: string;
  detail: string;
  status: string;
  due: string;
  details: string;
  submittedAt: string;
  history: string[];
  parentReferenceId?: string;
  parentType?: string;
};

type WorkflowProcessInstance = {
  id?: number;
  name?: string;
  referenceId?: string;
  referenceType?: string;
  status?: string;
  data?: string | Record<string, unknown>;
  createdAt?: string;
  createdBy?: string;
};

function userTicketType(row: WorkflowProcessInstance, data: Record<string, unknown>) {
  const referenceType = String(row.referenceType ?? "").toUpperCase();
  const concern = String(data.concern ?? "");
  if (referenceType === "CORRECTION" || concern === "correction") return "Correction";
  if (referenceType === "ERASURE" || concern === "erasure") return "Erasure";
  return "Grievance";
}

function ticketCompletable(status: string) {
  return status !== "Completed" && status !== "Rejected" && status !== "Cancelled" && status !== "Closed";
}

function workflowAuth() {
  const auth = getAuthData() as { token?: string; token_type?: string; systemUserId?: string | number } | null;
  const userId = auth?.systemUserId == null ? "" : String(auth.systemUserId);
  if (!auth?.token || !userId) throw new Error("Sign in again to manage user tickets.");
  return { auth, userId };
}

function workflowHeaders(userId: string, token: string, tokenType?: string) {
  return {
    accept: "application/json",
    "content-type": "application/json",
    Authorization: `${tokenType || "Bearer"} ${token}`,
    "x-nc-tool-id": workflowToolId,
    "x-nc-user-id": userId,
    "x-nc-system-user-id": userId,
  };
}

async function runWorkflowService<T>(serviceName: string, input: Record<string, unknown>): Promise<T> {
  const { auth, userId } = workflowAuth();
  const response = await fetch(
    `${workflowBaseUrl}/realm/${workflowRealmId}/user/${encodeURIComponent(userId)}/v1/execution/service/run/${serviceName}`,
    {
      method: "POST",
      headers: workflowHeaders(userId, auth.token!, auth.token_type),
      body: JSON.stringify({ input }),
    },
  );
  const text = await response.text();
  if (!response.ok) throw new Error(text || `${serviceName} failed (${response.status})`);
  return (text ? JSON.parse(text) : null) as T;
}

function taskInstancesFromResponse(body: unknown): Array<{ id?: number; status?: string }> {
  const visit = (node: unknown, depth = 0): Array<{ id?: number; status?: string }> => {
    if (depth > 8 || node == null) return [];
    if (Array.isArray(node)) {
      const rows = node.filter((item) => item && typeof item === "object" && ("id" in item || "status" in item)) as Array<{ id?: number; status?: string }>;
      if (rows.length) return rows;
      for (const item of node) {
        const found = visit(item, depth + 1);
        if (found.length) return found;
      }
      return [];
    }
    if (typeof node !== "object") return [];
    const record = node as Record<string, unknown>;
    for (const child of [record.data, record.payload, record.result, record.content, record.taskInstances]) {
      const found = visit(child, depth + 1);
      if (found.length) return found;
    }
    if ("id" in record && ("status" in record || "processInstanceId" in record || "taskId" in record)) {
      return [record as { id?: number; status?: string }];
    }
    return [];
  };
  return visit(body);
}

function openTaskInstanceId(body: unknown) {
  const open = taskInstancesFromResponse(body).find((row) => {
    const status = String(row.status ?? "").toUpperCase();
    return row.id != null && (status === "PENDING" || status === "IN_PROGRESS" || status === "CREATED" || status === "OPEN");
  });
  return open?.id ?? null;
}

function ticketDueLabel(from: Date, days = 30) {
  const due = new Date(from.getTime());
  due.setUTCDate(due.getUTCDate() + days);
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "long", timeZone: "UTC" }).format(due);
}

function humanizeTicketStatus(status: string) {
  if (status === "IN_PROGRESS") return "In progress";
  if (status === "COMPLETED") return "Completed";
  if (status === "REJECTED") return "Rejected";
  if (status === "CANCELLED") return "Cancelled";
  if (status === "CREATED") return "Submitted";
  return status || "Submitted";
}

function parseProcessInstanceData(data: WorkflowProcessInstance["data"]): Record<string, unknown> {
  let current: unknown = data;
  for (let depth = 0; depth < 3; depth += 1) {
    if (current == null) return {};
    if (typeof current === "string") {
      const trimmed = current.trim();
      if (!trimmed) return {};
      try {
        current = JSON.parse(trimmed);
        continue;
      } catch {
        return {};
      }
    }
    if (typeof current !== "object" || Array.isArray(current)) return {};
    const record = current as Record<string, unknown>;
    const nested = record.data;
    const looksLikeTicketPayload =
      record.userId != null ||
      record.concern != null ||
      record.concernLabel != null ||
      record.parentReferenceId != null ||
      record.parent_reference_id != null ||
      record.details != null;
    if (!looksLikeTicketPayload && nested != null && (typeof nested === "string" || typeof nested === "object")) {
      current = nested;
      continue;
    }
    return record;
  }
  return {};
}

function parentLinkFromTicketData(data: Record<string, unknown>, detailsText = "") {
  const rawId = data.parentReferenceId ?? data.parent_reference_id ?? data.parentTicketId ?? data.parentId;
  let parentReferenceId = String(rawId ?? "").trim();
  let parentType = String(data.parentType ?? data.parent_type ?? "").trim();
  if (!parentReferenceId) {
    const match = detailsText.match(/Linked previous request:\s*([^\s(]+)(?:\s*\(([^)]+)\))?/i);
    parentReferenceId = match?.[1]?.trim() ?? "";
    if (!parentType) parentType = match?.[2]?.trim() ?? "";
  }
  return {
    ...(parentReferenceId ? { parentReferenceId } : {}),
    ...(parentType ? { parentType } : {}),
  };
}

function looksLikeProcessInstance(row: unknown): row is WorkflowProcessInstance {
  if (!row || typeof row !== "object") return false;
  const item = row as Record<string, unknown>;
  return (
    item.referenceId != null ||
    item.referenceType != null ||
    item.processId != null ||
    (item.id != null && (item.data != null || item.status != null))
  );
}

function processInstancesFromWorkflowResponse(body: unknown): WorkflowProcessInstance[] {
  const visit = (node: unknown, depth = 0): WorkflowProcessInstance[] => {
    if (depth > 8 || node == null) return [];
    if (Array.isArray(node)) {
      if (node.length === 0) return [];
      if (node.every(looksLikeProcessInstance)) return node;
      for (const item of node) {
        const found = visit(item, depth + 1);
        if (found.length > 0) return found;
      }
      return [];
    }
    if (typeof node !== "object") return [];
    const record = node as Record<string, unknown>;
    for (const child of [record.payload, record.data, record.tickets, record.processInstances, record.result, record.content]) {
      const found = visit(child, depth + 1);
      if (found.length > 0) return found;
    }
    return [];
  };
  return visit(body);
}

function toUserTicket(row: WorkflowProcessInstance): UserTicket {
  const data = parseProcessInstanceData(row.data);
  const type = userTicketType(row, data);
  const concernLabel = String(data.concernLabel ?? data.concern ?? row.name ?? `${type} request`);
  const details = String(data.details ?? "").trim();
  const parentLink = parentLinkFromTicketData(data, details);
  const status = humanizeTicketStatus(String(row.status ?? "CREATED"));
  const created = row.createdAt ? new Date(row.createdAt) : new Date();
  const submittedAt = new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(created);
  const history = [`Submitted · ${submittedAt}`];
  if (status === "In progress") history.push("Under review");
  if (status === "Completed") history.push(`Completed · ${submittedAt}`);
  if (status === "Rejected") history.push("Rejected");
  return {
    id: row.referenceId?.trim() || `PI-${row.id ?? 0}`,
    processInstanceId: Number(row.id ?? 0) || undefined,
    userId: String(data.userId ?? row.createdBy ?? "").trim(),
    type,
    detail: concernLabel,
    status,
    due: ticketDueLabel(created),
    details,
    submittedAt,
    history,
    ...parentLink,
  };
}

function seedUserTickets(): UserTicket[] {
  return requestSeed.map((row, index) => ({
    id: row.id,
    processInstanceId: 9000 + index,
    userId: row.owner,
    type: row.type,
    detail: row.detail,
    status: row.status,
    due: row.due,
    details: row.detail,
    submittedAt: row.history[0]?.replace(/^Submitted · /, "") ?? "",
    history: [...row.history],
  }));
}

export async function listUserTickets(): Promise<UserTicket[]> {
  if (state.mockData) return seedUserTickets();
  const { auth, userId } = workflowAuth();
  const requestId = globalThis.crypto?.randomUUID?.() ?? `req-${Date.now()}`;
  const numericUserId = Number(userId);
  const response = await fetch(
    `${workflowBaseUrl}/realm/${workflowRealmId}/user/${encodeURIComponent(userId)}/v1/execution/request`,
    {
      method: "POST",
      headers: workflowHeaders(userId, auth.token!, auth.token_type),
      body: JSON.stringify({
        userId: Number.isFinite(numericUserId) ? numericUserId : userId,
        requestId,
        workflowName: "user-tickets-details",
        dataMap: {
          input: {
            request: {
              processIds: [grievanceProcessId],
              referenceTypes: ["GRIEVANCE", "CORRECTION", "ERASURE"],
              deleted: false,
            },
          },
        },
      }),
    },
  );
  const text = await response.text();
  if (!response.ok) throw new Error(text || `Could not load user tickets (${response.status})`);
  const body = text ? JSON.parse(text) as unknown : null;
  return processInstancesFromWorkflowResponse(body)
    .sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? "")))
    .map(toUserTicket);
}

export async function completeUserTicket(ticket: UserTicket, comment = ""): Promise<void> {
  if (!ticketCompletable(ticket.status)) throw new Error("This ticket is already closed.");
  if (state.mockData) return;
  if (!ticket.processInstanceId) throw new Error("Missing process instance for this ticket.");
  const { userId } = workflowAuth();
  const numericUserId = Number(userId);
  const tasksBody = await runWorkflowService<unknown>("cyborg-get-ticket", {
    processInstanceId: ticket.processInstanceId,
  });
  const taskInstanceId = openTaskInstanceId(tasksBody);
  if (taskInstanceId == null) throw new Error("No open task found to complete for this ticket.");
  await runWorkflowService("cyborg-change-status", {
    id: taskInstanceId,
    action: "APPROVE",
    userId: Number.isFinite(numericUserId) ? numericUserId : undefined,
    comment: comment.trim() || `Completed from Consent portal · ${ticket.id}`,
  });
}

export { ticketCompletable };
